/* ============================================
   PWALayout — Mobile-first layout for PWA
   Replaces Sidebar with bottom navigation.
   Shows Conversaciones + Wiki MAPRO modules.
   ============================================ */

import { useEffect, useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import ChatNotificationProvider from '@/components/layout/ChatNotificationProvider'
import ToastContainer from '@/components/layout/Toast'
import { clearRouteMemory } from '@/hooks/useRouteMemory'
import PullToRefresh from '@/components/pwa/PullToRefresh'
import { usePushNotifications } from '@/hooks/usePushNotifications'
import QRScanner from '@/components/pwa/QRScanner'

export default function PWALayout() {
  const { signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const push = usePushNotifications()
  const [showBanner, setShowBanner] = useState(false)
  const [showScanner, setShowScanner] = useState(false)

  const isChat = location.pathname.startsWith('/conversaciones')
  const isWiki = location.pathname.startsWith('/wiki')

  async function handleLogout() {
    clearRouteMemory()
    await signOut()
    navigate('/login', { replace: true })
  }

  // Auto-subscribe if already granted, or show banner after 2s
  useEffect(() => {
    if (push.loading || !push.isSupported) return

    if (push.permission === 'granted' && !push.isSubscribed) {
      // Already granted — subscribe silently
      push.subscribe()
    } else if (push.permission === 'default') {
      // Not asked yet — show banner after 2s
      const t = setTimeout(() => setShowBanner(true), 2000)
      return () => clearTimeout(t)
    }
  }, [push.loading, push.isSupported, push.permission, push.isSubscribed])

  async function handleEnableNotifications() {
    setShowBanner(false)
    await push.subscribe()
  }

  return (
    <ChatNotificationProvider>
      <div className="pwa-layout" id="pwa-layout">
        {/* Push notification permission banner */}
        {showBanner && (
          <div className="pwa-push-banner">
            <span>🔔 ¿Recibir notificaciones de nuevos mensajes?</span>
            <div className="pwa-push-banner__actions">
              <button
                className="pwa-push-banner__btn pwa-push-banner__btn--yes"
                onClick={handleEnableNotifications}
                type="button"
              >
                Activar
              </button>
              <button
                className="pwa-push-banner__btn pwa-push-banner__btn--no"
                onClick={() => setShowBanner(false)}
                type="button"
              >
                Ahora no
              </button>
            </div>
          </div>
        )}

        {/* Main content — pull-to-refresh wraps the outlet */}
        <main className="pwa-main">
          <PullToRefresh>
            <Outlet />
          </PullToRefresh>
        </main>

        {/* Bottom Navigation */}
        <nav className="pwa-bottom-nav">
          <button
            className={`pwa-bottom-nav__item${isChat ? ' pwa-bottom-nav__item--active' : ''}`}
            type="button"
            onClick={() => navigate('/conversaciones')}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
            </svg>
            <span>Chat</span>
          </button>

          <button
            className={`pwa-bottom-nav__item${isWiki ? ' pwa-bottom-nav__item--active' : ''}`}
            type="button"
            onClick={() => navigate('/wiki')}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /><path d="M8 7h8" /><path d="M8 11h6" />
            </svg>
            <span>Wiki</span>
          </button>

          <button
            className="pwa-bottom-nav__item"
            type="button"
            onClick={() => setShowScanner(true)}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="8" height="8" rx="1" /><rect x="14" y="2" width="8" height="8" rx="1" /><rect x="2" y="14" width="8" height="8" rx="1" /><rect x="14" y="14" width="4" height="4" /><line x1="22" y1="18" x2="22" y2="22" /><line x1="18" y1="22" x2="22" y2="22" />
            </svg>
            <span>QR</span>
          </button>

          <button
            className={`pwa-bottom-nav__item`}
            type="button"
            onClick={handleLogout}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span>Salir</span>
          </button>
        </nav>
        {showScanner && <QRScanner isOpen={showScanner} onClose={() => setShowScanner(false)} />}
      </div>
      <ToastContainer />
    </ChatNotificationProvider>
  )
}
